/**
 * Deciding an Items to save proposal — the click-to-decision wiring shared by
 * the desktop card and the phone's "Worth saving?" card.
 *
 * Two rules both cards follow:
 * - Eddy hears about a decision only once the server actually made it. The
 *   route answers `ok: false` with a 200 when nothing was decided (decided in
 *   another tab, or Eddy re-proposed meanwhile); telling Eddy "I approved"
 *   then makes him save a list nobody approved.
 * - After an edited approve, what Eddy is told comes from the server's
 *   response — the list as stored — never the local draft (FRONT-2321).
 *
 * Plain functions over the mutation's `mutate`, so the wiring is testable
 * without a DOM to click through.
 */
import { ApiError } from '~/lib/api-client';
import type { SaveProposalItem } from '~/hooks/queries/use-save-proposal';
import type {
  SaveProposalDecisionResponse,
  useApproveSaveProposal,
  useRejectSaveProposal,
} from '~/hooks/mutations/use-save-proposal-decision';
import type { SaveListEdit } from './save-proposal-edit-draft';

type ApproveMutate = ReturnType<typeof useApproveSaveProposal>['mutate'];
type RejectMutate = ReturnType<typeof useRejectSaveProposal>['mutate'];

export interface SaveDecisionCallbacks {
  /** The server decided. `editedItems` only when it stored the user's edit. */
  onDecided: (decision: 'approve' | 'reject', opts?: { editedItems?: SaveProposalItem[] }) => void;
  /** Nothing was decided — the proposal changed or was already answered. */
  onStale?: () => void;
  /** The server refused the edit, with a sentence for the person. */
  onRefused?: (message: string) => void;
  /** Any other failure (network, 5xx). The hook also toasts it; this lets
   *  an open form say so where the person is looking. */
  onFailed?: () => void;
}

/**
 * What a settled approve means. `null`: nothing was decided. `editedItems`
 * only when the server stored the user's edited list.
 */
export function approvedOutcome(
  data: SaveProposalDecisionResponse | undefined,
): { editedItems?: SaveProposalItem[] } | null {
  if (!data?.ok) return null;
  return data.edited && data.items ? { editedItems: data.items } : {};
}

export function approveSaveProposal(args: {
  approve: ApproveMutate;
  sessionId: string;
  edit?: SaveListEdit;
} & SaveDecisionCallbacks): void {
  const { approve, sessionId, edit, onDecided, onStale, onRefused, onFailed } = args;
  approve(
    { sessionId, ...(edit ? { edit } : {}) },
    {
      onSuccess: (data) => {
        const outcome = approvedOutcome(data);
        if (!outcome) onStale?.();
        else onDecided('approve', outcome.editedItems ? { editedItems: outcome.editedItems } : undefined);
      },
      onError: (err) => {
        if (edit && err instanceof ApiError && err.status === 400) onRefused?.(err.message);
        else onFailed?.();
      },
    },
  );
}

export function rejectSaveProposal(args: {
  reject: RejectMutate;
  sessionId: string;
} & SaveDecisionCallbacks): void {
  const { reject, sessionId, onDecided, onStale, onFailed } = args;
  reject(
    { sessionId },
    {
      onSuccess: (data) => {
        if (data?.ok) onDecided('reject');
        else onStale?.();
      },
      onError: () => onFailed?.(),
    },
  );
}
